import React, { useEffect, useMemo, useState } from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import IconButton from "@mui/material/IconButton";
import { FormControlLabel, Switch } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import toast from "react-hot-toast";
import {
  DEFAULT_HOME_SECTION_ORDER,
  resolveHomeSectionOrder,
} from "constants/homeSectionOrder";

function SectionToggleRow({
  title,
  description,
  checked,
  onToggle,
  toggleDisabled,
  hasTheme,
  isOverlay = false,
  dragHandleProps = null,
  showDragHandle = true,
  showArrows = true,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  reorderDisabled,
  style = {},
}) {
  return (
    <SoftBox
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 2.5,
        borderTop: isOverlay ? "none" : "1px solid",
        borderColor: "divider",
        bgcolor: isOverlay ? "background.paper" : "transparent",
        boxShadow: isOverlay ? "0 8px 24px rgba(15, 23, 42, 0.18)" : "none",
        borderRadius: isOverlay ? "8px" : 0,
        px: isOverlay ? 2 : 0,
        opacity: reorderDisabled && showDragHandle ? 0.85 : 1,
        ...style,
      }}
    >
      {showDragHandle ? (
        <SoftBox
          {...dragHandleProps}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: "6px",
            bgcolor: reorderDisabled ? "grey.100" : "grey.50",
            color: reorderDisabled ? "text.disabled" : "text.secondary",
            cursor: reorderDisabled ? "not-allowed" : "grab",
            flexShrink: 0,
            alignSelf: "flex-start",
            mt: 0.25,
            "&:active": { cursor: reorderDisabled ? "not-allowed" : "grabbing" },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 20 }} />
        </SoftBox>
      ) : null}

      <SoftBox sx={{ flex: "1 1 280px", minWidth: 0 }}>
        <SoftTypography variant="body2" fontWeight={600} color="text">
          {title}
        </SoftTypography>
        {description ? (
          <SoftTypography
            variant="caption"
            sx={{ opacity: 0.75, display: "block", mt: 0.5 }}
          >
            {description}
          </SoftTypography>
        ) : null}
      </SoftBox>

      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={onToggle}
            disabled={toggleDisabled || isOverlay}
            color="primary"
          />
        }
        label={!hasTheme ? "No theme" : checked ? "Shown" : "Hidden"}
        labelPlacement="start"
        sx={{ marginRight: 0, gap: 1, flexShrink: 0 }}
      />

      {showArrows ? (
        <SoftBox
          sx={{
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            alignSelf: "center",
          }}
        >
          <IconButton
            size="small"
            disabled={reorderDisabled || isFirst || isOverlay}
            onClick={onMoveUp}
            aria-label={`Move ${title} up`}
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={reorderDisabled || isLast || isOverlay}
            onClick={onMoveDown}
            aria-label={`Move ${title} down`}
            sx={{ p: 0.25 }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </SoftBox>
      ) : null}
    </SoftBox>
  );
}

function SortableSectionItem({
  section,
  disabled,
  hasTheme,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
    position: "relative",
    zIndex: isDragging ? 0 : 1,
  };

  return (
    <SoftBox ref={setNodeRef} style={style}>
      <SectionToggleRow
        title={section.title}
        description={section.description}
        checked={section.checked}
        onToggle={section.onToggle}
        toggleDisabled={section.toggleDisabled}
        hasTheme={hasTheme}
        dragHandleProps={{ ...attributes, ...listeners }}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        isFirst={isFirst}
        isLast={isLast}
        reorderDisabled={disabled}
      />
    </SoftBox>
  );
}

/**
 * @param {object} props
 * @param {Array<{ id: string, title: string, description?: string, checked: boolean, onToggle: Function, toggleDisabled?: boolean }>} props.sections
 *   Sortable home sections (must use ids from homeSectionOrder).
 * @param {Array<{ id: string, title: string, description?: string, checked: boolean, onToggle: Function, toggleDisabled?: boolean }>} [props.fixedSections]
 *   Non-sortable rows rendered below (e.g. Homepage Optin).
 */
function HomeSectionOrderEditor({
  contentData,
  handelThemeData,
  buttonLoading,
  selectedTheme,
  sections = [],
  fixedSections = [],
}) {
  const [sectionOrder, setSectionOrder] = useState(() =>
    resolveHomeSectionOrder(contentData?.homeSectionOrder)
  );
  const [activeId, setActiveId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isSaving || activeId) return;
    setSectionOrder(resolveHomeSectionOrder(contentData?.homeSectionOrder));
  }, [selectedTheme?.theme_id, contentData?.homeSectionOrder, isSaving, activeId]);

  const disabled = buttonLoading || !selectedTheme?.theme_id || isSaving;
  const hasTheme = Boolean(selectedTheme?.theme_id);

  const sectionsById = useMemo(() => {
    const map = {};
    sections.forEach((section) => {
      map[section.id] = section;
    });
    return map;
  }, [sections]);

  const orderedSections = useMemo(
    () =>
      sectionOrder
        .map((id) => sectionsById[id])
        .filter(Boolean),
    [sectionOrder, sectionsById]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const saveOrder = async (newOrder) => {
    if (!selectedTheme?.theme_id) {
      toast.error("Theme is not configured for this agency.");
      return;
    }

    setSectionOrder(newOrder);
    setIsSaving(true);
    try {
      await handelThemeData({ content: { homeSectionOrder: newOrder } });
      toast.success("Home page section order updated.");
    } finally {
      setIsSaving(false);
    }
  };

  const moveItem = (fromIndex, toIndex) => {
    if (disabled || fromIndex === toIndex) return;
    saveOrder(arrayMove(sectionOrder, fromIndex, toIndex));
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id || disabled) return;

    const oldIndex = sectionOrder.indexOf(active.id);
    const newIndex = sectionOrder.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    saveOrder(arrayMove(sectionOrder, oldIndex, newIndex));
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  const handleResetToDefault = () => {
    saveOrder([...DEFAULT_HOME_SECTION_ORDER]);
  };

  const activeSection = activeId ? sectionsById[activeId] : null;
  const activeIndex = activeId ? sectionOrder.indexOf(activeId) : -1;

  return (
    <SoftBox>
      <SoftTypography
        variant="caption"
        sx={{ opacity: 0.75, display: "block", mb: 1 }}
      >
        Drag by the handle or use the arrows to reorder sections. Toggle Shown / Hidden to
        control visibility. Community content like blog posts and news can go above business
        listings.
      </SoftTypography>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={sectionOrder}
          strategy={verticalListSortingStrategy}
        >
          <SoftBox sx={{ display: "flex", flexDirection: "column" }}>
            {orderedSections.map((section, index) => (
              <SortableSectionItem
                key={section.id}
                section={section}
                disabled={disabled}
                hasTheme={hasTheme}
                isFirst={index === 0}
                isLast={index === orderedSections.length - 1}
                onMoveUp={() => moveItem(index, index - 1)}
                onMoveDown={() => moveItem(index, index + 1)}
              />
            ))}
          </SoftBox>
        </SortableContext>

        <DragOverlay dropAnimation={{ duration: 200, easing: "ease" }}>
          {activeSection ? (
            <SectionToggleRow
              title={activeSection.title}
              description={activeSection.description}
              checked={activeSection.checked}
              onToggle={activeSection.onToggle}
              toggleDisabled
              hasTheme={hasTheme}
              isOverlay
              isFirst={activeIndex === 0}
              isLast={activeIndex === orderedSections.length - 1}
              reorderDisabled
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      {fixedSections.map((section) => (
        <SectionToggleRow
          key={section.id}
          title={section.title}
          description={section.description}
          checked={section.checked}
          onToggle={section.onToggle}
          toggleDisabled={section.toggleDisabled}
          hasTheme={hasTheme}
          showDragHandle={false}
          showArrows={false}
        />
      ))}

      <SoftBox sx={{ mt: 2, pt: 1 }}>
        <SoftButton
          variant="outlined"
          color="secondary"
          size="small"
          disabled={disabled}
          onClick={handleResetToDefault}
        >
          Reset to city-first order
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

export default HomeSectionOrderEditor;
